import { useState, useEffect } from 'react';
import queryString from 'qs';
import { makeStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router';

import Grid from '@material-ui/core/Grid';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import CheckIcon from '@material-ui/icons/CheckCircle';

import Typography from 'components/CncUI/Typography';
import Alert from 'components/CncUI/Alert';
import Button from 'components/CncUI/Button';
import FailureAlert from 'components/FailureAlert';
import Loader from 'components/Loader';

import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import ImageCapture from 'pages/OfferRegistration/steps/CaptureDocument/ImageCapture';
import Logger from 'utilities/logger';

import cncLogo from 'images/MyCast&CrewLogo.svg';
import FrontImage from 'pages/OfferRegistration/img/MobileFront.svg';
import BackImage from 'pages/OfferRegistration/img/MobileBack.svg';
import SelfieImage from 'pages/OfferRegistration/img/MobileSelfie.svg';

const useStyles = makeStyles(({ palette }) => ({
  root: {
    flexGrow: 1,
    paddingBottom: 80,
  },
  header: {
    backgroundColor: palette.mycc.main,
    boxShadow: 'none',
    position: 'initial',
  },
  logo: {
    flexGrow: 1,
    textAlign: 'center',
    '& img': {
      marginTop: -6,
    },
  },
  pictures: {
    textAlign: 'center',
    marginTop: 24,
  },
  button: {
    width: 230,
  },
  completeIcon: {
    color: palette.cncUI.alert.green.primary,
    fontSize: 54,
  },
}));

const CaptureImage = props => {
  const classes = useStyles();
  const { location } = props;
  const { search = '' } = location;
  const { token } = queryString.parse(search.slice(1));
  const logger = Logger('Capture Images');
  const [state, setState] = useState({
    complete: false,
    loading: true,
    data: {},
    error: null,
    auto: true,
  });
  const getQRCode = useQuery('getQRCode', {
    scopes: null,
    request: token => ({ token }),
    response: res => res.data.getQRCode || {},
  });
  const visitQRCode = useMutation('visitQRCode', {
    scopes: null,
    request: token => ({ token }),
    response: res => res.data.visitQRCode,
  });
  const saveQRCode = useMutation('saveQRCode', {
    scopes: null,
    request: (token, type, data) => ({ token, type, data }),
    response: res => res.data.saveQRCode,
  });
  const update = data => setState(old => ({ ...old, ...data }));
  const updateImage = (type, value) =>
    update({ data: { ...state.data, [type]: value } });

  const startCapture = type => {
    update({ error: null });
    const onSuccess = image => {
      updateImage(type, image);
      saveQRCode(token, type, image)
        .then(saved => {
          if (!saved) {
            updateImage(type, null);
            update({
              error: 'The image could not be saved, please try again.',
            });
          }
          logger.debug(`Saved ${type}`, saved);
        })
        .catch(err => {
          logger.error(err);
          update({ error: err.message });
        });
    };
    const onError = err => {
      update({ error: err.message });
      if (err !== 'Capture timeout') {
        update({ auto: false });
      }
      window.scrollTo(0, 0);
    };
    ImageCapture({ type, onSuccess, onError, isAuto: state.auto });
  };

  useEffect(() => {
    if (token) {
      getQRCode(token).then(data => {
        visitQRCode(token)
          .then(visited => logger.debug('Visited', visited))
          .catch(err => logger.error(err));
        logger.debug('Document', data);
        update({ data, loading: false });
      });
    }
  }, [token]);

  if (!token) {
    return (
      <FailureAlert
        message={`An error has ocurred,
          please try to read the QR Code again. (CODE 01)`}
      />
    );
  }

  if (state.loading) {
    return <Loader fullScreen message="Loading capture information" />;
  }

  if (!state.data.id) {
    return (
      <FailureAlert
        message={`An error has ocurred,
          please try to read the QR Code again. (CODE 02)`}
      />
    );
  }

  const disabled =
    !state.data.front ||
    !state.data.live ||
    (state.data.type !== 'Passport' && !state.data.back);

  return (
    <div className={classes.root}>
      <AppBar className={classes.header}>
        <Toolbar>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h6" className={classes.logo}>
                <img alt="logo" align="center" src={cncLogo} />
              </Typography>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {state.complete ? (
        <Grid container spacing={2} className={classes.pictures}>
          <Grid item xs={12}>
            <CheckIcon className={classes.completeIcon} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h3">
              Complete go back to your desktop and select "Verify Documents"
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">(You can close this window)</Typography>
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography align="center" variant="h4" style={{ marginTop: 24 }}>
                Capture Image
              </Typography>
            </Grid>
            {state.error && (
              <Grid
                item
                xs={12}
                id="CaptureImage-Error"
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <Alert fullWidth error style={{ width: 260 }}>
                  <Typography bold noMargin>
                    Something went wrong
                  </Typography>
                  {state.error.replace('GraphQL error: ', '')}
                </Alert>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={8} className={classes.pictures}>
            <Grid item xs={12}>
              <Grid container spacing={2} justify="center">
                <Grid item xs={12}>
                  <Typography noMargin bold>
                    Front of Document
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <img src={FrontImage} alt="Front" />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    className={classes.button}
                    onClick={() => startCapture('front')}
                  >
                    {state.data.front ? 'Retake' : 'Take Image'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            {state.data.type !== 'Passport' && (
              <Grid item xs={12}>
                <Grid container spacing={2} justify="center">
                  <Grid item xs={12}>
                    <Typography noMargin bold>
                      Back of Document
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <img src={BackImage} alt="Back" />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      className={classes.button}
                      onClick={() => startCapture('back')}
                    >
                      {state.data.back ? 'Retake' : 'Take Image'}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container spacing={2} justify="center">
                <Grid item xs={12}>
                  <Typography noMargin bold>
                    Take Selfie
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <img src={SelfieImage} alt="Selfie" />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    className={classes.button}
                    onClick={() => startCapture('live')}
                  >
                    {state.data.live ? 'Retake' : 'Take Image'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ textAlign: 'center', marginTop: 24 }}>
              <Button
                secondary
                disabled={disabled}
                className={classes.button}
                onClick={() => update({ complete: true })}
              >
                Next
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default withRouter(CaptureImage);
